// src/pages/DoorsEditorPage.jsx
// Admin page: Settings → Manage Doors (Catalogue by Door Type)
// Fixes:
// - Removed nested <button> error (place "Add ..." buttons in panel header area, not inside AccordionButton)
// - Limits only enforced for Extras/Accessories (no undefined warnAt)
// - Visibility defaults are auto-persisted; Save button is disabled until changes are made

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Text,
  Select,
  Switch,
  FormControl,
  FormLabel,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Wrap,
  WrapItem,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  Spacer,
} from '@chakra-ui/react';

import useOrgClaims from '../hooks/useOrgClaims';

import {
  DOOR_TYPES,
  ORG_LIMITS,
  DEFAULT_VISIBLE_TYPES,
  listMasterItems,
  listOrgItems,
  overlayAdmin,
  getVisibleTypes,
  setVisibleTypes,
  getOrgAddOnCounts,
  isDuplicateName,
  createOrgItem,
  updateOrgItem,
  archiveOrgItem,
  ensureOrgOverrideForMaster,
} from '../api/doorsApi';

const SECTION_ORDER = ['variant', 'door_colour', 'frame_colour', 'extra', 'accessory'];
const SECTION_LABELS = {
  variant: 'Variants',
  door_colour: 'Door Colours',
  frame_colour: 'Frame Colours',
  extra: 'Extras',
  accessory: 'Accessories',
};
const ADDABLE_SECTIONS = ['variant', 'door_colour', 'frame_colour', 'extra', 'accessory'];

export default function DoorsEditorPage() {
  const toast = useToast();
  const { orgId, loading: claimsLoading } = useOrgClaims(); // real orgId
  const ready = !claimsLoading && !!orgId;

  const [type, setType] = useState('uPVC');
  const [showArchived, setShowArchived] = useState(false);

  const [visibleTypes, setVTypes] = useState({ ...DEFAULT_VISIBLE_TYPES });
  const [vtDirty, setVtDirty] = useState(false);

  const [master, setMaster] = useState([]);
  const [orgItems, setOrgItems] = useState([]);
  const [overlay, setOverlay] = useState([]);
  const [counts, setCounts] = useState({ extras: 0, accessories: 0 });
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { id?, __source, ...fields }
  const [sectionForAdd, setSectionForAdd] = useState('variant');

  // Form state for modal
  const [fType, setFType] = useState('uPVC');
  const [fName, setFName] = useState('');
  const [fUnit, setFUnit] = useState('each');
  const [fPrice, setFPrice] = useState(0);
  const [fActive, setFActive] = useState(true);
  const [fSort, setFSort] = useState(0);

  // ----- Load initial when orgId ready -----
  useEffect(() => {
    if (!ready) return;
    (async () => {
      try {
        setLoading(true);
        const [vt, mAll, oAll, cts] = await Promise.all([
          getVisibleTypes(orgId), // now also persists defaults if missing
          listMasterItems(),
          listOrgItems(orgId),
          getOrgAddOnCounts(orgId),
        ]);
        setVTypes(vt);
        setVtDirty(false); // freshly loaded, nothing to save
        setMaster(mAll);
        setOrgItems(oAll);
        setCounts(cts);
      } catch (err) {
        console.error(err);
        toast({ status: 'error', title: 'Failed to load Doors data.' });
      } finally {
        setLoading(false);
      }
    })();
  }, [ready, orgId, toast]);

  // ----- Recompute overlay -----
  useEffect(() => {
    setOverlay(overlayAdmin(master, orgItems));
  }, [master, orgItems]);

  // Items for selected type
  const itemsForType = useMemo(() => {
    const list = overlay
      .filter((x) => x.type === type)
      .filter((x) => (showArchived ? true : x.active !== false))
      .sort((a, b) => {
        const sa = typeof a.sort === 'number' ? a.sort : 0;
        const sb = typeof b.sort === 'number' ? b.sort : 0;
        if (sa !== sb) return sa - sb;
        return (a.name || '').localeCompare(b.name || '');
      });
    const grouped = {
      variant: [],
      door_colour: [],
      frame_colour: [],
      extra: [],
      accessory: [],
    };
    for (const it of list) grouped[it.kind]?.push(it);
    return grouped;
  }, [overlay, type, showArchived]);

  // ----- Helpers -----
  const isAddOnKind = (k) => k === 'extra' || k === 'accessory';
  const addOnCounts = counts; // {extras, accessories}

  async function refresh() {
    if (!ready) return;
    const [mAll, oAll, cts] = await Promise.all([
      listMasterItems(),
      listOrgItems(orgId),
      getOrgAddOnCounts(orgId),
    ]);
    setMaster(mAll);
    setOrgItems(oAll);
    setCounts(cts);
  }

  async function handleSaveVisibleTypes() {
    if (!ready || !vtDirty) return;
    try {
      await setVisibleTypes(orgId, visibleTypes);
      setVtDirty(false);
      toast({ status: 'success', title: 'Visible door types updated for sales reps.' });
    } catch (e) {
      console.error(e);
      toast({ status: 'error', title: 'Failed to save visible types.' });
    }
  }

  function openAdd(k) {
    setEditing(null);
    setSectionForAdd(k);
    setFType(type);
    setFName('');
    setFUnit(k === 'door_colour' ? '%' : 'each');
    setFPrice(0);
    setFActive(true);
    // sort: add at end
    const current = itemsForType[k] || [];
    const maxSort = current.reduce(
      (m, it) => Math.max(m, typeof it.sort === 'number' ? it.sort : 0),
      0
    );
    setFSort(maxSort + 10);
    setModalOpen(true);
  }

  function openEdit(item) {
    setEditing(item);
    setSectionForAdd(item.kind);
    setFType(item.type);
    setFName(item.name);
    setFUnit(item.unit || 'each');
    setFPrice(typeof item.price === 'number' ? item.price : 0);
    setFActive(item.active !== false);
    setFSort(typeof item.sort === 'number' ? item.sort : 0);
    setModalOpen(true);
  }

  async function handleSubmitModal() {
    if (!ready) return;

    const name = fName.trim();
    const kind = sectionForAdd;
    const typeSel = fType;

    if (!name) {
      toast({ status: 'warning', title: 'Please provide a name.' });
      return;
    }

    // Duplicate guard for Type+Kind+Name
    // Pass the ID of the item being edited (if any) to exclude it from the check.
    const isDuplicate = await isDuplicateName(orgId, typeSel, kind, name, editing?.id);

    if (isDuplicate) {
      toast({
        status: 'error',
        title: 'That name already exists in this section for this Door Type.',
      });
      return;
    }

    // Limits on org-created add-ons (ONLY for extras/accessories)
    if (isAddOnKind(kind) && (!editing || editing.__source !== 'org')) {
      const limitCfg = ORG_LIMITS[kind]; // guaranteed to exist for extra/accessory
      const currCount = kind === 'extra' ? addOnCounts.extras : addOnCounts.accessories;

      if (currCount >= limitCfg.hardMax) {
        toast({
          status: 'error',
          title: `Limit reached for ${kind === 'extra' ? 'Extras' : 'Accessories'}.`,
          description: `You can have up to ${limitCfg.hardMax}. Archive an item to free a slot.`,
        });
        return;
      }
      if (currCount >= limitCfg.warnAt) {
        toast({
          status: 'warning',
          title: `You’re near the limit for ${kind === 'extra' ? 'Extras' : 'Accessories'}.`,
        });
      }
    }

    const payload = {
      kind,
      type: typeSel,
      name,
      unit: fUnit,
      price: Number(fPrice) || 0,
      active: fActive,
      sort: Number.isFinite(+fSort) ? +fSort : 0,
    };

    try {
      if (!editing) {
        // Create new ORG item
        await createOrgItem(orgId, payload);

        // If it's an add-on under uPVC/Composite, auto-create the twin in the other type (independent after)
        if (isAddOnKind(kind) && (typeSel === 'uPVC' || typeSel === 'Composite')) {
          const twinType = typeSel === 'uPVC' ? 'Composite' : 'uPVC';
          const existsTwin = await isDuplicateName(orgId, twinType, kind, name);
          if (!existsTwin) {
            await createOrgItem(orgId, { ...payload, type: twinType });
          }
        }
      } else if (editing.__source === 'org') {
        await updateOrgItem(orgId, editing.id, payload);
      } else {
        // editing a master row → create/update org override
        await ensureOrgOverrideForMaster(orgId, editing, payload);
      }

      setModalOpen(false);
      await refresh();
      toast({ status: 'success', title: 'Saved.' });
    } catch (e) {
      console.error(e);
      toast({ status: 'error', title: 'Save failed.' });
    }
  }

  async function handleArchive(item, makeActive) {
    if (!ready) return;
    try {
      if (item.__source === 'org') {
        await archiveOrgItem(orgId, item.id, makeActive);
        await refresh();
        toast({ status: 'success', title: makeActive ? 'Unarchived.' : 'Archived.' });
      } else {
        toast({
          status: 'info',
          title: 'Master items cannot be archived here. Edit to override instead.',
        });
      }
    } catch (e) {
      console.error(e);
      toast({ status: 'error', title: 'Action failed.' });
    }
  }

  // ----- Render -----
  const hiddenForReps = !visibleTypes[type];

  return (
    <Box p={6}>
      <Heading size="lg" mb={6} color="blue.600">
        Manage Doors
      </Heading>
      <Text mb={6} color="gray.600">
        Catalogue by Door Type. You can add, edit or delete Doors to match your products.
        Need Help? email us: developers@fixihub.pro
      </Text>

      {/* Visibility Panel (compact row) */}
      <Box borderWidth="1px" borderRadius="lg" p={4} mb={6}>
        <Heading size="md" mb={3}>
          Door Types visible to sales reps
        </Heading>
        <Wrap spacing={6} align="center">
          {DOOR_TYPES.map((t) => (
            <WrapItem key={t}>
              <HStack>
                <Text minW="80px">{t}</Text>
                <Switch
                  isChecked={!!visibleTypes[t]}
                  onChange={(e) => {
                    const next = { ...visibleTypes, [t]: e.target.checked };
                    setVTypes(next);
                    setVtDirty(true);
                  }}
                  isDisabled={!ready}
                />
              </HStack>
            </WrapItem>
          ))}
        </Wrap>
        <HStack mt={4} justify="flex-end">
          <Button
            onClick={handleSaveVisibleTypes}
            colorScheme="teal"
            isDisabled={!ready || !vtDirty}
          >
            Save visibility
          </Button>
        </HStack>
      </Box>

      {/* Top controls */}
      <HStack spacing={4} mb={4} wrap="wrap">
        <FormControl maxW="220px">
          <FormLabel>Door Type</FormLabel>
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            {DOOR_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl display="flex" alignItems="center" maxW="220px">
          <FormLabel m={0} mr={3}>
            Show archived
          </FormLabel>
          <Checkbox isChecked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
        </FormControl>

        {hiddenForReps && <Badge colorScheme="orange">Hidden from sales reps</Badge>}
      </HStack>

      {/* Sections */}
      <Accordion allowMultiple defaultIndex={[0, 1, 2, 3, 4]}>
        {SECTION_ORDER.map((k) => {
          const isAddOn = k === 'extra' || k === 'accessory';
          const count = isAddOn ? (k === 'extra' ? counts.extras : counts.accessories) : undefined;
          const limitCfg = isAddOn ? ORG_LIMITS[k] : undefined;

          return (
            <AccordionItem key={k} border="1px solid" borderColor="gray.200" rounded="md" mb={3}>
              <h2>
                <AccordionButton _expanded={{ bg: 'gray.50' }}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    {SECTION_LABELS[k]}
                    {isAddOn && (
                      <Badge ml={3} colorScheme="gray">
                        {k === 'extra' ? 'Extras' : 'Accessories'}: {count}
                      </Badge>
                    )}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>

              <AccordionPanel pb={4}>
                {/* Panel header with Add button (NOT inside AccordionButton) */}
                <Flex mb={3} align="center">
                  <Spacer />
                  {ADDABLE_SECTIONS.includes(k) && (
                    <Tooltip
                      label={
                        isAddOn && count >= limitCfg.hardMax
                          ? `Limit ${limitCfg.hardMax} reached`
                          : ''
                      }
                    >
                      <Button
                        size="sm"
                        onClick={() => openAdd(k)}
                        colorScheme="teal"
                        variant="outline"
                        isDisabled={!ready || (isAddOn && count >= limitCfg.hardMax)}
                      >
                        Add {SECTION_LABELS[k].slice(0, -1)}
                      </Button>
                    </Tooltip>
                  )}
                </Flex>

                <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Name</Th>
                        <Th isNumeric>Price</Th>
                        <Th>Unit</Th>
                        <Th>Active</Th>
                        <Th>Sort</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(itemsForType[k] || []).map((row) => (
                        <Tr key={`${row.__source}-${row.id || row.name}`}>
                          <Td>{row.name}</Td>
                          <Td isNumeric>
                            {typeof row.price === 'number' ? row.price.toFixed(2) : '0.00'}
                          </Td>
                          <Td>{row.unit || 'each'}</Td>
                          <Td>
                            <Badge colorScheme={row.active !== false ? 'green' : 'gray'}>
                              {row.active !== false ? 'YES' : 'NO'}
                            </Badge>
                          </Td>
                          <Td>{typeof row.sort === 'number' ? row.sort : 0}</Td>
                          <Td>
                            <HStack>
                              <Button size="xs" onClick={() => openEdit(row)} isDisabled={!ready}>
                                Edit
                              </Button>
                              <Tooltip
                                label={
                                  row.__source !== 'org' ? 'Master item: archive not allowed' : ''
                                }
                              >
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={() => handleArchive(row, !(row.active !== false))}
                                  isDisabled={!ready || row.__source !== 'org'}
                                >
                                  {row.active !== false ? 'Archive' : 'Unarchive'}
                                </Button>
                              </Tooltip>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}

                      {!loading && (itemsForType[k] || []).length === 0 && (
                        <Tr>
                          <Td colSpan={6}>
                            <Text py={6} textAlign="center" color="gray.500">
                              No items.
                            </Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Add/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editing ? 'Edit Item' : `Add ${SECTION_LABELS[sectionForAdd].slice(0, -1)}`}
          </ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Door Type</FormLabel>
                <Select value={fType} onChange={(e) => setFType(e.target.value)}>
                  {DOOR_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input value={fName} onChange={(e) => setFName(e.target.value)} />
              </FormControl>

              <FormControl>
                <FormLabel>Unit</FormLabel>
                <Select value={fUnit} onChange={(e) => setFUnit(e.target.value)}>
                  <option value="each">each</option>
                  <option value="%">%</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Price</FormLabel>
                <Input type="number" value={fPrice} onChange={(e) => setFPrice(e.target.value)} />
                {Number(fPrice) === 0 && (
                  <Text fontSize="sm" color="orange.500" mt={1}>
                    Price is £0 — this will show as £0 in the catalogue.
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Sort (order)</FormLabel>
                <Input type="number" value={fSort} onChange={(e) => setFSort(e.target.value)} />
              </FormControl>

              <HStack justify="space-between">
                <FormLabel m={0}>Active</FormLabel>
                <Switch isChecked={fActive} onChange={(e) => setFActive(e.target.checked)} />
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button onClick={() => setModalOpen(false)} variant="ghost">
                Cancel
              </Button>
              <Button colorScheme="teal" onClick={handleSubmitModal} isDisabled={!ready}>
                Save
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}